import { createFileRoute, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import Skeleton from '@/components/ui/Skeleton'
import type { CompanyDetail } from '@/features/company/api/companyDetail'
import { useSuspenseCompanyDetail } from '@/features/company/api/queries'
import {
  DEFAULT_FLOORSHEET_PERIOD,
  type FloorsheetPeriod,
} from '@/features/company/lib/floorsheetPeriod'
import type { MarketRange } from '@/features/market/api/marketApi'
import { useDailyBars } from '@/features/market/api/queries'
import { useMitraStore } from '@/features/mitra/store'
import { usePriceHistory } from '@/features/prices/api/queries'
import { useSetWatchlistSymbols } from '@/features/watchlist/api/mutations'
import { useWatchlistCollection } from '@/features/watchlist/api/queries'
import { cn } from '@/lib/utils'
import type { CandlePoint } from '@/schemas/market'
import AdvancedPanel from '@/screens/stock/components/AdvancedPanel'
import AgmHistoryPanel from '@/screens/stock/components/AgmHistoryPanel'
import AnnouncementsPanel from '@/screens/stock/components/AnnouncementsPanel'
import BrokerHoldingPanel from '@/screens/stock/components/BrokerHoldingPanel'
import ComparePanel from '@/screens/stock/components/ComparePanel'
import DividendPanel from '@/screens/stock/components/DividendPanel'
import EventsPanel from '@/screens/stock/components/EventsPanel'
import FinancialsPanel from '@/screens/stock/components/FinancialsPanel'
import FloorsheetPanel from '@/screens/stock/components/FloorsheetPanel'
import OverviewPanel from '@/screens/stock/components/OverviewPanel'
import { OverviewSkeleton } from '@/screens/stock/components/overview/OverviewSkeleton'
import PriceHistoryPanel from '@/screens/stock/components/PriceHistoryPanel'
import ReportsPanel from '@/screens/stock/components/ReportsPanel'
import ShareHoldingPanel from '@/screens/stock/components/ShareHoldingPanel'
import StockHeader from '@/screens/stock/components/StockHeader'
import StockHeaderSkeleton from '@/screens/stock/components/StockHeaderSkeleton'
import StockRail from '@/screens/stock/components/StockRail'
import StockSectionTabs from '@/screens/stock/components/StockSectionTabs'
import TechnicalPanel from '@/screens/stock/components/TechnicalPanel'
import { StockSearchContext, type StockSearch } from '@/screens/stock/lib/stockSearch'
import { TECHNICAL_COUNTBACK, initialStockPeriod } from '@/screens/stock/lib/tabReads'
import { loadStock, stockSearchSchema } from '@/screens/stock/StockScreen.loader'
import {
  ADVANCED_TABS,
  cleanCompareWith,
  DEFAULT_ADVANCED_TAB,
  type AdvancedTab,
  type StockTab,
} from '@/screens/stock/stockData'

/**
 * The company head over the page's own section strip: one element, as ScreenLayout's `header`
 * must be. 16 from the head to the strip, and the column's 24 from the strip's hairline to the
 * body.
 */
function StockHead({ head, symbol, tab }: { head: ReactNode; symbol: string; tab: StockTab }) {
  return (
    <div className="flex flex-col gap-4">
      {head}
      <StockSectionTabs symbol={symbol} tab={tab} />
    </div>
  )
}

/**
 * Tells Mitra this company's page is on screen while it is, so a question
 * asked from here ("yo kina ghatyo?") is about it (`AskRequest.page`).
 * `null` publishes nothing: a symbol no listed company has is not a company,
 * which is also why the pending page, before the read says, publishes none.
 */
function useCompanyPage(symbol: string | null) {
  const showCompany = useMitraStore((state) => state.showCompany)
  useEffect(() => (symbol === null ? undefined : showCompany(symbol)), [symbol, showCompany])
}

/**
 * The page before the company has loaded: the header's own skeleton (the
 * real ticker, from the URL) and the real section strip (the URL is all it
 * needs) over a body shaped like the tab being opened.
 */
function StockPending() {
  const { symbol } = Route.useParams()
  const { tab } = Route.useSearch()
  const ticker = symbol.toUpperCase()
  return (
    <ScreenLayout
      pending
      header={
        <StockHead head={<StockHeaderSkeleton symbol={ticker} />} symbol={ticker} tab={tab} />
      }
      rail={<StockRail symbol={symbol.toUpperCase()} />}
      railLabel={`About ${symbol.toUpperCase()}`}
      pageTitle={symbol.toUpperCase()}
    >
      <StockBodySkeleton tab={tab} />
    </ScreenLayout>
  )
}

/**
 * The Advanced strip's nine chips, at the widths the loaded strip draws them:
 * measured off the rendered chips (73, 76, 89, 69, 76, 94, 70, 84 and 81px, a
 * 775px strip with its 8px gaps) and snapped to the 4px grid. Keyed on the
 * sub-tab so a tenth cannot be added to the strip and forgotten here.
 */
const STRIP_CHIP_WIDTHS: Record<AdvancedTab, string> = {
  swot: 'w-18',
  health: 'w-19',
  earnings: 'w-22',
  value: 'w-17',
  charts: 'w-19',
  valuation: 'w-23',
  peers: 'w-17',
  returns: 'w-21',
  trading: 'w-20',
}

/**
 * The boxed strip at the top of Advanced: 36px chips, 8px apart. Clipped, not
 * scrolled — the loaded strip scrolls where it does not fit, and a skeleton
 * that did too would be a control with nothing behind it.
 */
function AdvancedStripSkeleton() {
  return (
    <div className="flex items-center gap-2 overflow-hidden">
      {ADVANCED_TABS.map((tab) => (
        <Skeleton
          key={tab.value}
          className={cn('h-9 shrink-0 rounded-sm', STRIP_CHIP_WIDTHS[tab.value])}
        />
      ))}
    </div>
  )
}

/**
 * The body under the header. Overview is its own loaded blocks, one for one,
 * and Mitra's banner under them: see `OverviewSkeleton`, which also says why
 * "Your position" is not among them.
 *
 * Advanced is its strip over one block: the strip is the same on all nine
 * sub-tabs, which is the part that is known before any of them has read.
 *
 * No tab has a Mitra's take footer (owner ruling C1, 2026-10-29: the take is
 * Overview's speech bubble only), so nothing follows the body.
 *
 * Every other tab gets one panel-sized block: each of those panels owns its
 * own read and its own skeleton, which takes over the moment the page
 * commits, so this only has to hold the space until then.
 */
function StockBodySkeleton({ tab }: { tab: StockTab }) {
  return tab === 'overview' ? (
    <OverviewSkeleton />
  ) : tab === 'advanced' ? (
    <div className="flex flex-col gap-6">
      <AdvancedStripSkeleton />
      <Skeleton className="h-90 w-full rounded-lg" />
    </div>
  ) : (
    <Skeleton className="h-90 w-full rounded-lg" />
  )
}

/** The copy the page used for a failed company read before it had a loader. */
function StockError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} title="Couldn't load this stock" />
}

export const Route = createFileRoute('/_app/stocks/$symbol')({
  validateSearch: stockSearchSchema,
  loader: loadStock,
  pendingComponent: StockPending,
  errorComponent: StockError,
  component: StockScreen,
})

/**
 * The tabs whose panel reads the shared price series.
 *
 * V2 removes the screen-level sparkline that sat under the price on every
 * tab: only Overview (`4236:73517`) and Technical (`4236:74874`) draw a chart,
 * and each draws its own. `price-history` draws its OWN reading of the same
 * series in `PriceHistoryPanel`, on the same key — it is on this list so the
 * screen's read and the panel's are one request, whichever mounts first (the
 * loader starts it for a deep link; see `prefetchStockTab`). The other ten
 * tabs do not fetch a series nothing on screen uses.
 *
 * Technical is NOT on this list. Its chart reads a different ENDPOINT — a
 * year of daily bars off `/charts/history/` — on a fixed bar count whatever
 * the range chip says, since the 200-day average it rules cannot be computed
 * from the 1W default. See its own reads below.
 */
const PRICE_SERIES_TABS: readonly StockTab[] = ['overview', 'price-history']

/**
 * Tab → panel. Returns an element and has no `default`, so an added tab fails
 * to compile until it has a case here.
 *
 * The nine panels that came with the company API take `{ symbol, company }` —
 * see `StockPanelProps` — and read for themselves. The live five predate that
 * and keep the props their own reads grew.
 */
function StockTabPanel({
  tab,
  symbol,
  company,
  onTabChange,
  period,
  onPeriodChange,
  points,
  seriesState,
  technical,
}: {
  tab: StockTab
  /** The URL's symbol, upper-cased — the one StockScreen starts each tab's
   *  read on, so a panel keyed on it reads that cache entry by construction. */
  symbol: string
  company: CompanyDetail
  onTabChange: (tab: StockTab) => void
  period: MarketRange
  onPeriodChange: (period: MarketRange) => void
  points: readonly number[]
  /** Whether `points` is the series for `period`, or one still on its way. */
  seriesState: 'loading' | 'failed' | 'loaded'
  /** Technical's own year of BARS — see PRICE_SERIES_TABS above — and its
   *  way out to Advanced › Trading. */
  technical: {
    bars: readonly CandlePoint[]
    state: 'loading' | 'failed' | 'loaded'
    onRetry: () => void
    onTabChange: (tab: StockTab) => void
  }
}): ReactElement {
  switch (tab) {
    case 'overview':
      return (
        <OverviewPanel
          symbol={symbol}
          company={company}
          points={points}
          seriesState={seriesState}
          period={period}
          onPeriodChange={onPeriodChange}
        />
      )
    case 'technical':
      return (
        <TechnicalPanel
          symbol={symbol}
          company={company}
          bars={technical.bars}
          seriesState={technical.state}
          onRetrySeries={technical.onRetry}
          onTabChange={technical.onTabChange}
        />
      )
    case 'floorsheet':
      return <FloorsheetPanel symbol={symbol} name={company.name} />
    case 'price-history':
      // Shares StockScreen's own `period` state and `usePriceHistory` key
      // rather than keeping its own — see PRICE_SERIES_TABS' comment above.
      return <PriceHistoryPanel symbol={symbol} period={period} onPeriodChange={onPeriodChange} />
    case 'broker-holding':
      return <BrokerHoldingPanel symbol={symbol} />
    case 'financials':
      return <FinancialsPanel symbol={symbol} company={company} onTabChange={onTabChange} />
    case 'advanced':
      return <AdvancedPanel symbol={symbol} company={company} />
    case 'events':
      return <EventsPanel symbol={symbol} company={company} onTabChange={onTabChange} />
    case 'dividend':
      return <DividendPanel symbol={symbol} company={company} />
    case 'share-holding':
      return <ShareHoldingPanel symbol={symbol} company={company} />
    case 'agm-history':
      return <AgmHistoryPanel symbol={symbol} company={company} />
    case 'announcements':
      return <AnnouncementsPanel symbol={symbol} company={company} />
    case 'compare':
      return <ComparePanel symbol={symbol} company={company} />
    case 'reports':
      return <ReportsPanel symbol={symbol} company={company} />
  }
}

/** The URL keys a panel changes through `useStockSearch`; `undefined` clears one. */
type StockSearchChange = Partial<{
  sub: AdvancedTab | undefined
  fy: string | undefined
  q: string | undefined
  with: string[] | undefined
  fp: FloorsheetPeriod | undefined
}>

function StockScreen() {
  const { symbol: rawSymbol } = Route.useParams()
  const symbol = rawSymbol.toUpperCase()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  // Always here: the loader cached it, a first-load failure is the route's
  // error page, and a failed refetch keeps it (a suspense query only throws
  // while it has no data). `null` is still possible — see "not found" below.
  const { data: company } = useSuspenseCompanyDetail(symbol)
  useCompanyPage(company === null ? null : symbol)
  const watchlistsQuery = useWatchlistCollection()
  const setWatchlistSymbols = useSetWatchlistSymbols()
  // Seeded from the tab the member arrives on, before the first render and
  // so before the first request — see `initialStockPeriod` for why the two
  // tabs that share this state open on different ranges.
  const [period, setPeriod] = useState<MarketRange>(() => initialStockPeriod(search.tab))
  const tab = search.tab
  // Only fetched for the tabs whose panel actually reads it — see
  // PRICE_SERIES_TABS. On `price-history`, PriceHistoryPanel shares this same
  // hook and this state, so the two calls land on one key and one request.
  const priceHistory = usePriceHistory(symbol, period, {
    enabled: PRICE_SERIES_TABS.includes(tab),
  })
  // Technical's chart, on a fixed year of bars: the 200-day average it rules
  // needs 200 sessions, which no chip shorter than a year can supply. The
  // loader starts it when the URL opens on this tab; this hook owns it from
  // then on, and starts it for a member who switches to it. The panel reads
  // its figures (`/technicals/`) for itself.
  const technicalBars = useDailyBars(symbol, TECHNICAL_COUNTBACK, {
    enabled: tab === 'technical',
  })
  const lists = watchlistsQuery.data?.lists ?? []
  const watched = lists.some((list) => list.rows.some((row) => row.symbol === symbol))
  // The part of the URL a panel owns — see `StockSearchContext`. Written with
  // `...prev`, unlike `setTab` below, which starts a fresh URL on purpose: a
  // sub-tab change keeps the period, and a period change keeps the sub-tab.
  // SWOT, today's floorsheet and an empty `with` need no handling: the schema
  // parses each to nothing, so none is ever written back.
  const sub = search.sub ?? DEFAULT_ADVANCED_TAB
  const floorsheetPeriod = search.fp ?? DEFAULT_FLOORSHEET_PERIOD
  // The schema cannot know this page's symbol, so the screen takes it out of
  // the picks — a panel then never sees its own company among "the others".
  const compareWith = useMemo(
    () => cleanCompareWith(search.with ?? [], symbol),
    [search.with, symbol],
  )
  const stockSearch = useMemo<StockSearch>(() => {
    const write = (change: StockSearchChange, replace = false) => {
      void navigate({
        search: (prev) => ({ ...prev, ...change }),
        ...(replace ? { replace: true } : {}),
      })
    }
    return {
      sub,
      fy: search.fy,
      q: search.q,
      compareWith,
      floorsheetPeriod,
      setSub: (next) => {
        if (next !== sub) write({ sub: next, with: undefined })
      },
      setPeriod: (next) => {
        write(next)
      },
      setCompareWith: (next) => {
        const picks = cleanCompareWith(next, symbol)
        write({ with: picks.length === 0 ? undefined : picks }, true)
      },
      setFloorsheetPeriod: (next) => {
        if (next !== floorsheetPeriod)
          write({ fp: next === DEFAULT_FLOORSHEET_PERIOD ? undefined : next })
      },
    }
  }, [navigate, sub, search.fy, search.q, compareWith, floorsheetPeriod, symbol])

  if (company === null)
    return (
      <ScreenLayout>
        <EmptyState
          title="Stock not found"
          description={`No listed company has the symbol ${symbol}.`}
          action={
            <Link to="/market/stock-prices" className="text-accent">
              Browse stock prices
            </Link>
          }
        />
      </ScreenLayout>
    )

  const setTab = (value: StockTab) => {
    void navigate({ search: { tab: value } })
  }
  // Technical's "Advanced ›" names what Trading shows — the floor and
  // ceiling, how easy it is to trade, whether it beat its group — so it opens
  // that sub-tab rather than Advanced's default, SWOT.
  const openFromTechnical = (value: StockTab) => {
    if (value === 'advanced') void navigate({ search: { tab: 'advanced', sub: 'trading' } })
    else setTab(value)
  }
  const points = priceHistory.data?.points.map((point) => Number(point.value)) ?? []
  // Whether `points` answers for `period`. Data before error, as Technical's series does it: a
  // refetch that fails keeps the line. A range `keepPreviousData` is still standing in for is not
  // an answer yet, so the chart does not call it empty while the real one is out.
  const seriesState =
    priceHistory.data === undefined
      ? priceHistory.isError
        ? 'failed'
        : 'loading'
      : priceHistory.isPlaceholderData
        ? 'loading'
        : 'loaded'

  return (
    <ScreenLayout
      rail={<StockRail symbol={symbol} sectorSymbol={company.profile.sectorSymbol} />}
      railLabel={`About ${symbol}`}
      pageTitle={symbol}
      header={
        <StockHead
          head={
            <StockHeader
              symbol={symbol}
              company={company}
              onTabChange={setTab}
              lists={lists}
              watched={watched}
              setWatchlistSymbols={setWatchlistSymbols}
            />
          }
          symbol={symbol}
          tab={tab}
        />
      }
    >
      <StockSearchContext value={stockSearch}>
        <StockTabPanel
          key={tab}
          tab={tab}
          symbol={symbol}
          company={company}
          onTabChange={setTab}
          period={period}
          onPeriodChange={setPeriod}
          points={points}
          seriesState={seriesState}
          technical={{
            bars: technicalBars.data?.bars ?? [],
            // Data before error: a refetch that fails after the year has
            // loaded keeps the chart and the lines ruled from it.
            state:
              technicalBars.data !== undefined
                ? 'loaded'
                : technicalBars.isError
                  ? 'failed'
                  : 'loading',
            onRetry: () => {
              void technicalBars.refetch()
            },
            onTabChange: openFromTechnical,
          }}
        />
      </StockSearchContext>
    </ScreenLayout>
  )
}
